import { StrictMode, useState, useEffect, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Sidebar } from "./components/Sidebar";
import { LoginScreen } from "./components/LoginScreen";
import type { AgentResponse } from "./components/AskDataDrawer";
import { startConversation, askAgent, endConversation } from "./api/actions";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { ThemeProvider } from "./theme";
import { GLOBAL_CSS } from "./constants";
import { logBuildInfo } from "./buildInfo";
import type { View } from "./types";
import { ToastProvider } from "./components/ToastProvider";
import { useAdCopyJobsWatcher } from "./hooks/useAdCopyJobs";

// ── Lazy-load each view to keep the initial bundle small ────────────────────
const DashboardView      = lazy(() => import("./views/DashboardView").then(m => ({ default: m.DashboardView })));
const DetailView         = lazy(() => import("./views/DetailView").then(m => ({ default: m.DetailView })));
const WizardView         = lazy(() => import("./views/WizardView").then(m => ({ default: m.WizardView })));
const ContentLibraryView = lazy(() => import("./views/ContentLibraryView").then(m => ({ default: m.ContentLibraryView })));
const LauncherView       = lazy(() => import("./views/LauncherView").then(m => ({ default: m.LauncherView })));
const LauncherTrackerView = lazy(() => import("./views/LauncherTrackerView").then(m => ({ default: m.LauncherTrackerView })));
const AdCopyReviewView   = lazy(() => import("./views/AdCopyReviewView").then(m => ({ default: m.AdCopyReviewView })));
const AnalyticsView      = lazy(() => import("./views/AnalyticsView").then(m => ({ default: m.AnalyticsView })));
const RunHistoryView     = lazy(() => import("./views/RunHistoryView").then(m => ({ default: m.RunHistoryView })));
const AutomationRulesView = lazy(() => import("./views/AutomationRulesView").then(m => ({ default: m.AutomationRulesView })));
const PerformancePage    = lazy(() => import("./views/PerformancePage").then(m => ({ default: m.PerformancePage })));
const AdminPanelView     = lazy(() => import("./views/AdminPanelView").then(m => ({ default: m.AdminPanelView })));
const InvoicesPage       = lazy(() => import("./views/InvoicesPage").then(m => ({ default: m.InvoicesPage })));
const CreatorsAdminView  = lazy(() => import("./views/CreatorsAdminView").then(m => ({ default: m.CreatorsAdminView })));
const PartnerNetworkView = lazy(() => import("./views/PartnerNetworkView").then(m => ({ default: m.PartnerNetworkView })));
const InternalReportView = lazy(() => import("./views/InternalReportView").then(m => ({ default: m.InternalReportView })));
const CreatorCRMView     = lazy(() => import("./views/CreatorCRMView").then(m => ({ default: m.CreatorCRMView })));
// Lazy so vega-embed (its chart renderer, a heavy dep) only loads when Ask Data is actually opened.
const AskDataDrawer      = lazy(() => import("./components/AskDataDrawer").then(m => ({ default: m.AskDataDrawer })));

// Map router paths ↔ View literals
const PATH_TO_VIEW: Record<string, View> = {
  "/":                  "dashboard",
  "/content":           "content",
  "/launcher":          "launcher",
  "/launcher-tracker":  "launcher_tracker",
  "/ad-copy-review":    "ad_copy_review",
  "/performance":       "performance",
  "/rules":             "rules",
  "/analytics":         "analytics",
  "/runs":              "runs",
  "/admin":             "admin",
  "/invoices":          "invoices",
  "/creators-admin":    "creators_admin",
  "/partner-network":   "partner_network",
  "/creator-crm":       "creator_crm",
  "/internal-report":   "internal_report",
};

const VIEW_TO_PATH: Record<View, string> = {
  dashboard:        "/",
  content:          "/content",
  launcher:         "/launcher",
  launcher_tracker: "/launcher-tracker",
  ad_copy_review:   "/ad-copy-review",
  performance:      "/performance",
  rules:            "/rules",
  analytics:        "/analytics",
  runs:             "/runs",
  admin:            "/admin",
  invoices:         "/invoices",
  creators_admin:   "/creators-admin",
  partner_network:  "/partner-network",
  creator_crm:      "/creator-crm",
  internal_report:  "/internal-report",
  detail:           "/",      // handled by parent
  new:              "/",      // handled by parent
  team:             "/",      // removed nav item
};

// ── QueryClient (shared across the whole app) ────────────────────────────────
const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

// Shown when a signed-in user holds no roles (locked out until an admin assigns one).
function NoAccessView() {
  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ maxWidth: 420, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: "var(--c-text)" }}>No access yet</div>
        <div style={{ fontSize: 14, lineHeight: 1.6, color: "var(--c-muted)" }}>
          Your account is signed in but hasn't been granted any roles. Ask an admin to assign you a
          role (Creator Management, Social Boosting, Invoicing, Partner Network, Social Brand
          (Internal), or Admin) and it'll take effect on your next sign-in.
        </div>
      </div>
    </div>
  );
}

// ── AppShell: sidebar + routed view area ─────────────────────────────────────
function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, isAdmin, canView } = useAuth();
  // Background poll for the ad-copy generation queue — fires the "your ads are ready" toast no
  // matter which view is currently mounted (see useAdCopyJobs.ts).
  useAdCopyJobsWatcher();

  // Derive active View from current path so Sidebar highlight stays correct
  const currentView: View =
    PATH_TO_VIEW[location.pathname] ?? "dashboard";

  // Where to send someone who hits a page they can't see: their first accessible nav page (in nav
  // order), or the no-access screen if their roles grant nothing. Guarding "/" against dashboard and
  // redirecting to homePath can never loop, because homePath is only ever a page canView allows.
  //
  // MUST list every view any role can grant, or a user whose ONLY page is missing here lands on
  // "no access" despite being able to use that page. That was already happening to an invoicing-only
  // user: `invoices` is a real nav button (Sidebar renders it separately from the NAV array, gated on
  // canView) and the route allows them in, but this list omitted it, so login dropped them on the
  // no-access screen and they had to click Invoices to get anywhere. Keep this in step with
  // ROLE_VIEWS, not just with the NAV array. (URL Creator is not a routed page — it's a modal
  // inside Launcher, so it has no entry here.)
  const homePath =
    ([["dashboard", "/"], ["content", "/content"], ["launcher", "/launcher"],
      ["launcher_tracker", "/launcher-tracker"], ["ad_copy_review", "/ad-copy-review"],
      ["performance", "/performance"],
      ["rules", "/rules"],
      ["invoices", "/invoices"],
      ["partner_network", "/partner-network"],
      ["creator_crm", "/creator-crm"],
      ["internal_report", "/internal-report"]] as [View, string][])
      .find(([v]) => canView(v))?.[1] ?? "/no-access";

  // Gate a route element by the page's view: render it if allowed, else redirect to homePath.
  const guard = (view: View, el: JSX.Element): JSX.Element =>
    canView(view) ? el : <Navigate to={homePath} replace />;

  const handleNavigate = (v: View) => {
    const path = VIEW_TO_PATH[v] ?? "/";
    navigate(path);
  };

  const currentUser = user?.email ?? undefined;

  // ── Ask Data (Cortex assistant): a global overlay reachable from any view ──────
  // The conversation lives in socialiq-ai (Google-managed, keyed by conversationId); we hold only
  // the handle + the last turn here. Errors are shown as an agent message so a missing backend
  // (503 until the staging Data Agent + IAM land) is visible, not silent.
  const [askOpen, setAskOpen] = useState(false);
  // Minimized hides the drawer without ending the conversation, so the page underneath (e.g. the
  // Performance filters) stays usable while an answer is pending.
  const [askMinimized, setAskMinimized] = useState(false);
  const [askUnread, setAskUnread] = useState(false);
  const [conversationId, setConversationId] = useState("");
  const [lastResponse, setLastResponse] = useState<AgentResponse | null>(null);
  const [askLoading, setAskLoading] = useState(false);

  const handleAskStart = async () => {
    try {
      const r = await startConversation();
      setConversationId(r.conversation_id);
      setLastResponse(null);
    } catch (e) {
      setLastResponse({ answer: (e as Error).message });
    }
  };

  const handleAsk = async (question: string) => {
    setAskLoading(true);
    try {
      let cid = conversationId;
      if (!cid) { cid = (await startConversation()).conversation_id; setConversationId(cid); }
      setLastResponse(await askAgent(cid, question));
    } catch (e) {
      setLastResponse({ answer: (e as Error).message, has_chart: false });
    } finally {
      setAskLoading(false);
    }
  };

  const handleAskEnd = () => {
    if (conversationId) void endConversation(conversationId);
    setConversationId("");
    setLastResponse(null);
    setAskOpen(false);
    setAskMinimized(false);
  };

  // An answer that lands while minimized flags the launcher until the drawer is restored.
  useEffect(() => {
    if (askMinimized && lastResponse) setAskUnread(true);
  }, [lastResponse]);

  const handleAskLauncher = () => {
    if (askOpen) {
      setAskMinimized(false);
      setAskUnread(false);
    } else {
      setAskOpen(true);
    }
  };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: "var(--c-bg)" }}>
      <Sidebar view={currentView} onNavigate={handleNavigate} currentUser={currentUser} onSignOut={signOut} isAdmin={isAdmin} canView={canView} />

      <main style={{ flex: 1, overflow: "auto", padding: "24px", display: "flex", flexDirection: "column" }}>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={guard("dashboard", <DashboardView />)} />
            <Route path="/templates/new" element={guard("dashboard", <WizardView />)} />
            <Route path="/templates/:id/edit" element={guard("dashboard", <WizardView />)} />
            <Route path="/templates/:id" element={guard("dashboard", <DetailView />)} />
            <Route path="/content" element={guard("content", <ContentLibraryView />)} />
            <Route path="/launcher" element={guard("launcher", <LauncherView />)} />
            <Route path="/launcher-tracker" element={guard("launcher_tracker", <LauncherTrackerView />)} />
            <Route path="/ad-copy-review" element={guard("ad_copy_review", <AdCopyReviewView />)} />
            <Route path="/performance" element={guard("performance", <PerformancePage />)} />
            <Route path="/rules" element={guard("rules", <AutomationRulesView />)} />
            <Route path="/analytics" element={guard("analytics", <AnalyticsView />)} />
            <Route path="/runs" element={guard("runs", <RunHistoryView />)} />
            <Route path="/admin" element={isAdmin ? <AdminPanelView /> : <Navigate to={homePath} replace />} />
            <Route path="/invoices" element={guard("invoices", <InvoicesPage />)} />
            <Route path="/creators-admin" element={guard("creators_admin", <CreatorsAdminView />)} />
            <Route path="/partner-network" element={guard("partner_network", <PartnerNetworkView />)} />
            <Route path="/creator-crm" element={guard("creator_crm", <CreatorCRMView />)} />
            <Route path="/internal-report" element={guard("internal_report", <InternalReportView />)} />
            <Route path="/no-access" element={<NoAccessView />} />
            <Route path="*" element={<Navigate to={homePath} replace />} />
          </Routes>
        </Suspense>
      </main>

      {/* Floating "Ask Data" launcher — global entry point for the Cortex assistant. */}
      {(!askOpen || askMinimized) && (
        <button
          onClick={handleAskLauncher}
          title={askMinimized ? "Restore your Ask Data conversation" : "Ask Data — natural-language analytics over your warehouse"}
          style={{
            position: "fixed", bottom: 24, right: 24, zIndex: 40,
            display: "flex", alignItems: "center", gap: 8, padding: "12px 18px",
            border: "none", borderRadius: 999, cursor: "pointer",
            background: "var(--c-accent)", color: "#fff", fontSize: 14, fontWeight: 600,
            fontFamily: "inherit", boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
          }}
        >
          ✨ Ask Data
          {askMinimized && askLoading && <span style={{ fontWeight: 400, opacity: 0.85 }}>· Thinking…</span>}
          {askMinimized && askUnread && !askLoading && (
            <span aria-label="New answer" style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff" }} />
          )}
        </button>
      )}

      {askOpen && (
        <Suspense fallback={null}>
          <AskDataDrawer
            open={askOpen}
            minimized={askMinimized}
            onClose={() => { setAskOpen(false); setAskMinimized(false); }}
            onMinimize={() => setAskMinimized(true)}
            onStartConversation={handleAskStart}
            onAsk={handleAsk}
            onEndConversation={handleAskEnd}
            conversationId={conversationId}
            lastResponse={lastResponse}
            loading={askLoading}
            activeView={currentView}
          />
        </Suspense>
      )}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, height: "100%" }}>
      <div style={{ width: 24, height: 24, border: "2px solid var(--c-border)", borderTopColor: "var(--c-accent)", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    </div>
  );
}


// ── Auth gate: spinner while resolving, login when signed out, app when in ─────
function Gate() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--c-bg)" }}>
        <LoadingSpinner />
      </div>
    );
  }
  if (!user) return <LoginScreen />;

  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

// ── Root ─────────────────────────────────────────────────────────────────────
function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={qc}>
        <ToastProvider>
          <AuthProvider>
            <Gate />
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

// Inject global styles and CSS variables
const styleEl = document.createElement("style");
styleEl.textContent = GLOBAL_CSS;
document.head.appendChild(styleEl);

logBuildInfo();  // one-line build banner (version · sha · build time) in the console

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
